import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { addressLine, countryName } from "@/lib/address";
import { APP_NAME } from "@/config";
import { Card, Label11, StatusBadge, usd } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ExternalLink } from "lucide-react";

// Store → Billing history (backend billing.history), like Railway's: every payment to the company's plan and credits,
// newest first, each with its invoice. Lemon Squeezy issues the invoices as merchant of record, so the link is theirs,
// asked for at the click: with the company's saved billing details (name, address, a note such as a VAT number) an
// invoice PDF made with them; without, a plan payment's own invoice PDF or a top-up's receipt page.
const PAGE = 25;
// a table from 1024px (the sidebar takes its share); below it each payment stacks (description and date, then amount and status)
const COLS = "grid grid-cols-[92px_minmax(0,1fr)_88px_120px_104px] gap-3.5 px-4 py-2.5";
const STATUS = { paid: ["Paid", "info"], "partly refunded": ["Partly refunded", "warning"], refunded: ["Refunded", "muted"] };
const REASON = { first: "first payment", renewal: "renewal", change: "plan change" };
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const money = (cents, currency) => (!currency || currency === "USD" ? usd(cents / 100) : `${(cents / 100).toFixed(2)} ${currency}`);

// ISO 3166-1 alpha-2, named by the browser in English
const COUNTRY_CODES = ("AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY "
  + "BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD "
  + "GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN "
  + "KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE "
  + "NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM "
  + "SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT "
  + "ZA ZM ZW").split(" ");
const COUNTRIES = COUNTRY_CODES.map((code) => [code, countryName(code)]).sort((a, b) => a[1].localeCompare(b[1]));
const NEEDS_STATE = ["US", "CA"];  // Lemon Squeezy needs a state for these (backend billing_routes.NEEDS_STATE)
const EMPTY = { name: "", address: "", city: "", state: "", zip_code: "", country: "", notes: "" };

function useBillingDetails() {
  return useQuery({ queryKey: ["billing-details"], queryFn: async () => (await api.get("/store/billing-details")).data.details });
}

function BillingDetailsDialog({ open, onOpenChange, details }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { if (open) setForm({ ...EMPTY, ...(details || {}) }); }, [open, details]);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const done = (saved, message) => { qc.setQueryData(["billing-details"], saved); onOpenChange(false); toast.success(message); };
  const save = useMutation({
    mutationFn: async () => (await api.put("/store/billing-details", Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k].trim()])))).data,
    onSuccess: ({ details: saved }) => done(saved, "Saved. Your invoices show these details."),
    onError: (e) => toast.error(apiErr(e)),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete("/store/billing-details")).data,
    onSuccess: () => done(null, "Removed. Your invoices show what you gave at checkout."),
    onError: (e) => toast.error(apiErr(e)),
  });
  const state = NEEDS_STATE.includes(form.country);
  const ready = ["name", "address", "city", "zip_code", "country"].every((k) => form[k].trim()) && (!state || form.state.trim());
  const busy = save.isPending || remove.isPending;
  const field = (key, label, props = {}, hint) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`billing-${key}`}>{label}</Label>
      <Input id={`billing-${key}`} data-testid={`billing-${key}`} value={form[key]} onChange={set(key)} {...props} />
      {hint && <span className="text-sm text-muted-foreground">{hint}</span>}
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-[18px] max-h-[90vh] overflow-y-auto">
        <div className="flex flex-col gap-1">
          <DialogTitle>Billing details</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground [text-wrap:pretty]">
            What your invoices say, past payments' too. Lemon Squeezy makes each invoice when you open it; {APP_NAME} sends it these details then.
          </DialogDescription>
        </div>
        <form className="flex flex-col gap-[14px]" onSubmit={(e) => { e.preventDefault(); if (ready && !busy) save.mutate(); }}>
          {field("name", "Company or full name", { maxLength: 120, autoComplete: "organization", placeholder: "e.g. Acme Bakery Ltd" })}
          {field("address", "Street address", { maxLength: 200, autoComplete: "street-address" })}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
            {field("city", "City", { maxLength: 100, autoComplete: "address-level2" })}
            {field("zip_code", "Postcode", { maxLength: 20, autoComplete: "postal-code" })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
            <div className="flex flex-col gap-1.5">
              <Label>Country</Label>
              <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
                <SelectTrigger data-testid="billing-country"><SelectValue placeholder="Choose a country" /></SelectTrigger>
                <SelectContent>{COUNTRIES.map(([code, name]) => <SelectItem key={code} value={code}>{name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {field("state", state ? "State" : "State or region (optional)", { maxLength: 100, autoComplete: "address-level1" })}
          </div>
          {field("notes", "Note on your invoices (optional)", { maxLength: 300, placeholder: "e.g. VAT number FR12345678901" },
            "A tax or VAT number, a purchase order number, or anything your accountant asks for.")}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            {details ? (
              <button type="button" className="text-sm text-muted-foreground transition-colors hover:text-gr-danger" disabled={busy}
                onClick={() => remove.mutate()} data-testid="billing-remove">Remove details</button>
            ) : <span className="text-sm text-muted-foreground">{ready ? "" : state ? "Fill in every field but the note." : "Fill in every field but the state and the note."}</span>}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" aria-disabled={!ready} disabled={busy} data-testid="billing-save">
                {save.isPending && <Loader2 className="animate-spin" />}Save
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BillingDetailsCard({ details, loading }) {
  const [editing, setEditing] = useState(false);
  return (
    <Card className="px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="billing-details">
      <div className="flex flex-col gap-0.5 min-w-0 text-sm">
        <Label11>On your invoices</Label11>
        {loading ? <span className="text-muted-foreground">Loading…</span> : details ? (<>
          <span className="text-base text-foreground">{details.name}</span>
          <span className="text-muted-foreground [overflow-wrap:anywhere]">{addressLine(details)}</span>
          {details.notes && <span className="text-muted-foreground [overflow-wrap:anywhere]">{details.notes}</span>}
        </>) : (
          <span className="text-muted-foreground [text-wrap:pretty]">The name and country you gave at checkout. Add your company's name and address to put them on your invoices.</span>
        )}
      </div>
      <Button variant="outline" onClick={() => setEditing(true)} disabled={loading} data-testid="billing-details-edit">
        {details ? "Edit" : "Add billing details"}
      </Button>
      <BillingDetailsDialog open={editing} onOpenChange={setEditing} details={details} />
    </Card>
  );
}

function what(p, wallet) {
  if (p.kind === "topup") return wallet ? "AI wallet top-up" : "Credits top-up";
  if (p.kind === "plan") return `${p.plan || "Plan"} plan${p.reason ? `, ${REASON[p.reason]}` : ""}`;
  return p.kind;
}

export default function BillingHistory({ wallet }) {
  const [limit, setLimit] = useState(PAGE);
  const q = useQuery({
    queryKey: ["billing-history", limit], placeholderData: (previous) => previous,
    queryFn: async () => (await api.get("/store/billing-history", { params: { limit } })).data,
  });
  const open = useMutation({
    mutationFn: async (id) => (await api.get(`/store/billing-history/${encodeURIComponent(id)}/document`)).data,
    onSuccess: ({ url }) => window.open(url, "_blank", "noopener,noreferrer"),
    onError: (e) => (e?.response?.status === 404 ? toast.info(apiErr(e)) : toast.error(apiErr(e))),
  });
  const billing = useBillingDetails();
  const invoiced = !!billing.data;  // a top-up gets an invoice PDF too once the company has billing details
  const payments = q.data?.payments || [];

  return (
    <section id="billing-history" className="flex flex-col gap-3 scroll-mt-4" data-testid="billing-history">
      <div className="flex flex-col gap-0.5">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Billing history</span>
        <span className="text-sm text-muted-foreground [text-wrap:pretty]">
          Every payment for your {APP_NAME} plan and credits. Invoices come from Lemon Squeezy, our merchant of record.
        </span>
      </div>
      <BillingDetailsCard details={billing.data} loading={billing.isLoading} />
      <div className="bg-card rounded-[10px] text-sm tabular-nums">
        <div className={`hidden lg:grid ${COLS} border-b text-xs uppercase tracking-[0.09em] text-muted-foreground`}>
          <span>Date</span><span>Description</span><span className="text-right">Amount</span><span>Status</span><span className="text-right">Invoice</span>
        </div>
        {q.isLoading ? (
          <div className="px-4 py-3 flex items-center gap-1.5 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading your payments…</div>
        ) : q.isError ? (
          <div className="px-4 py-3 text-gr-danger">{apiErr(q.error)}</div>
        ) : payments.length === 0 ? (
          <div className="px-4 py-3 text-muted-foreground">No payments yet. Your plan and top-up payments show here.</div>
        ) : payments.map((p) => {
          const [word, tone] = STATUS[p.status] || STATUS.paid;
          const busy = open.isPending && open.variables === p.id;
          const description = <span className="text-base text-foreground">{what(p, wallet)}</span>;
          const credits = p.credits_usd > 0 && `${usd(p.credits_usd)} of credits added`;
          const amount = <span className="text-foreground">{money(p.total_cents, p.currency)}</span>;
          const tax = p.tax_cents > 0 && <span className="text-muted-foreground">incl. {money(p.tax_cents, p.currency)} tax</span>;
          const status = <StatusBadge status={p.status} label={word} tone={tone} />;
          const refunded = p.refunded_cents > 0 && p.status !== "refunded" && <span className="text-muted-foreground">{money(p.refunded_cents, p.currency)} refunded</span>;
          const link = p.id && (
            <button type="button" onClick={() => open.mutate(p.id)} disabled={open.isPending}
              className="inline-flex items-center gap-1 text-foreground underline-offset-2 hover:underline disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}{p.kind === "topup" && !invoiced ? "Receipt" : "View invoice"}
            </button>
          );
          return (
            <div key={p.id || p.date} className="border-b last:border-b-0" data-testid={`billing-row-${p.id}`}>
              <div className={`hidden lg:grid ${COLS} items-start`}>
                <span className="text-muted-foreground">{date(p.date)}</span>
                <div className="flex flex-col min-w-0">{description}{credits && <span className="text-muted-foreground">{credits}</span>}</div>
                <div className="flex flex-col text-right">{amount}{tax}</div>
                <div className="flex flex-col items-start gap-0.5">{status}{refunded}</div>
                <span className="text-right">{link}</span>
              </div>
              <div className="lg:hidden flex justify-between gap-3 px-4 py-3">
                <div className="flex flex-col items-start gap-0.5 min-w-0">
                  {description}
                  <span className="text-muted-foreground">{[date(p.date), credits].filter(Boolean).join(" · ")}</span>
                  {link && <span className="pt-1">{link}</span>}
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0 text-right">{amount}{tax}{status}{refunded}</div>
              </div>
            </div>
          );
        })}
      </div>
      {q.data?.more && (
        <Button variant="outline" className="self-start" onClick={() => setLimit((n) => n + PAGE)} disabled={q.isFetching} data-testid="billing-history-more">
          {q.isFetching && <Loader2 className="animate-spin" />}Show older payments
        </Button>
      )}
    </section>
  );
}
